/* eslint @typescript-eslint/ban-ts-comment:0 */

import React from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import ImageIcon from '@/components/common/ImageIcon';
import IconSizes from '@/constants/ui/IconSizes';
import useDebouncedEffect from '@/lib/hooks/useDebouncedEffect';
import translate from '@/lib/intl/translate';
import type CSVHeaderColumn from '../../types/CSVHeaderColumn';
import type CSVRow from '../../types/CSVRow';

const TableComponents = {
  Scroller: React.forwardRef((props, ref) => (
    // @ts-ignore
    <TableContainer {...props} ref={ref} />
  )),
  // @ts-ignore
  Table: (props) => <Table {...props} style={{ borderCollapse: 'separate' }} />,
  TableHead,
  TableRow,
  TableBody: React.forwardRef((props, ref) => (
    // @ts-ignore
    <TableBody {...props} ref={ref} />
  )),
};

export default function PreviewCsvDialog({
  headers,
  open,
  onClose,
  rows,
}: {
  headers: CSVHeaderColumn[];
  open: boolean;
  onClose: () => void;
  rows: Array<string[]>;
}) {
  const FixedHeaderContent = React.useCallback(
    () => (
      <TableRow>
        {headers.map((header) => (
          <TableCell
            key={header.id}
            className="w-[200px] max-w-[200px] bg-white p-2 text-xs"
          >
            {header.name}
          </TableCell>
        ))}
      </TableRow>
    ),
    [headers],
  );

  const ItemContent = React.useCallback(
    (index: number, row: string[]) => (
      <>
        {row.map((str, i) => {
          const key = `${headers[i].id}-${i}`;

          return (
            <TableCell
              id={key}
              key={key}
              className="w-[200px] max-w-[200px] truncate p-2"
            >
              {str}
            </TableCell>
          );
        })}
      </>
    ),
    [headers, rows],
  );

  const [filterValue, setFilterValue] = React.useState('');
  const [filteredRows, setFilteredRows] = React.useState<CSVRow[]>([]);
  const [tooltipTitle, setTooltipTitle] = React.useState('');

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilterValue(event.target.value.trim());
    },
    [],
  );

  const handleTooltipMouseMove = React.useCallback(
    (event: React.MouseEvent) => {
      const { target } = event;
      if (target instanceof HTMLTableCellElement && !tooltipTitle) {
        setTooltipTitle(target.innerText);
      } else if (tooltipTitle) {
        setTooltipTitle('');
      }
    },
    [],
  );

  React.useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  useDebouncedEffect(
    () => {
      if (!filterValue.length) {
        setFilteredRows(rows);
      }

      if (filterValue.length) {
        const filtered = [];
        const filterValueLower = filterValue.toLowerCase();

        for (let i = 0, l = rows.length; i < l; i += 1) {
          const row = rows[i];
          const rowStr = row.join('|').toLowerCase();

          if (rowStr.match(filterValueLower)) {
            filtered.push(row);
          }
        }

        setFilteredRows(filtered);
      }
    },
    [filterValue, rows],
    200,
  );

  return (
    <Dialog open={open} fullScreen>
      <div className="flex items-baseline justify-between">
        <DialogTitle variant="h3" className="type-h3 sm:type-h2">
          {translate('DATA_UPLOADS_PREVIEW_CSV')}
        </DialogTitle>

        <Button onClick={onClose}>
          <ImageIcon
            width={IconSizes.SM}
            height={IconSizes.SM}
            type="close"
            alt={translate('MULTI_ACTION_CLOSE')}
          />
        </Button>
      </div>
      <DialogContent className="flex h-full flex-col gap-2 p-1">
        <FormControl variant="outlined">
          <InputLabel htmlFor="search-csv">
            {translate('DATA_UPLOADS_PREVIEW_SEARCH')}
          </InputLabel>

          <OutlinedInput
            id="search-csv"
            label={translate('DATA_UPLOADS_PREVIEW_SEARCH')}
            onChange={handleInputChange}
          />
        </FormControl>

        <div className="flex-grow overflow-y-hidden">
          <Tooltip title={tooltipTitle}>
            <div className="h-full" onMouseMove={handleTooltipMouseMove}>
              <TableVirtuoso
                className="h-[calc(100vh-138px)]"
                // @ts-ignore
                data={filteredRows}
                // @ts-ignore
                components={TableComponents}
                fixedHeaderContent={FixedHeaderContent}
                itemContent={ItemContent}
              />
            </div>
          </Tooltip>
        </div>
      </DialogContent>
    </Dialog>
  );
}
